import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  XMarkIcon,
  HomeIcon,
  CogIcon,
  UsersIcon,
  CreditCardIcon,
  DocumentChartBarIcon,
  CircleStackIcon,
  ChevronDownIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../hooks/useAuth';
import { useSettings } from '../../contexts/SettingsContext';
import { usePermission } from '../../hooks/usePermission';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

const getNavigation = (isAdmin: boolean) => {
  const baseNav = [
    { name: 'Home', href: '/dashboard', icon: HomeIcon },
    {
      name: 'Setup',
      icon: CogIcon,
      children: [
        { name: 'Ingredient Families', href: '/setup/ingredient-families' },
        { name: 'Ingredients', href: '/setup/ingredients' },
        { name: 'Diets', href: '/setup/diets' },
        { name: 'Menus', href: '/setup/menus' },
        { name: 'Recipes', href: '/setup/recipes' },
        { name: 'Recipe Plates', href: '/setup/recipe-plates' },
        { name: 'Prices', href: '/setup/prices' },
        { name: 'Lookup Table', href: '/setup/lookup-table' },
        { name: 'Countries', href: '/setup/countries' },
        { name: 'Cities', href: '/setup/cities' },
        { name: 'Measure Units', href: '/setup/measure-units' },
        { name: 'Route Deliveries', href: '/setup/route-deliveries' },
      ],
    },
    { name: 'Clients', href: '/clients', icon: UsersIcon },
    { 
      name: 'Billing',
      icon: CreditCardIcon,
      children: [
        { name: 'Payments', href: '/payments' },
        { name: 'Receipts', href: '/receipts' },
      ]
    },
    { name: 'Reports', href: '/reports', icon: DocumentChartBarIcon },
    { name: 'Backup', href: '/backup', icon: CircleStackIcon },
  ];

  if (isAdmin) {
    baseNav.push({ name: 'Admin', href: '/admin', icon: ShieldCheckIcon });
  }

  return baseNav;
};

const SidebarContent: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { canView, isAdmin } = usePermission();
  const [expandedItems, setExpandedItems] = React.useState<string[]>(['Setup']);

  const navigation = getNavigation(isAdmin);

  // Map Setup submenu href to permission page name
  const getPermissionPageFromHref = (href: string): string => {
    const hrefToPermissionMap: Record<string, string> = {
      '/setup/ingredient-families': 'INGREDIENT_FAMILIES',
      '/setup/ingredients': 'INGREDIENTS',
      '/setup/diets': 'DIETS',
      '/setup/menus': 'MENUS',
      '/setup/recipes': 'RECIPES',
      '/setup/recipe-plates': 'RECIPE_PLATES',
      '/setup/prices': 'PRICES',
      '/setup/lookup-table': 'LOOKUP_TABLE',
      '/setup/countries': 'COUNTRIES',
      '/setup/cities': 'CITIES',
      '/setup/measure-units': 'MEASURE_UNITS',
      '/setup/route-deliveries': 'ROUTE_DELIVERIES',
    };
    return hrefToPermissionMap[href] || href.split('/')[1]?.toUpperCase() || '';
  };

  // Filter navigation items based on user permissions
  const filteredNavigation = navigation.map(item => {
    if (item.children) {
      // Filter child items based on permissions
      const filteredChildren = item.children.filter(child => {
        // Get the permission page name for this child item
        const permissionPage = getPermissionPageFromHref(child.href);
        // Check if user can view this page
        return canView(permissionPage);
      });

      // Only show parent if it has visible children
      return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
    } else {
      // For non-parent items, check if user has permission
      const pageName = item.href.split('/')[1]; // e.g., '/dashboard' -> 'dashboard'
      const pageNameUpper = pageName.toUpperCase();

      // Special handling for Reports page - check for any report sub-permissions
      if (pageNameUpper === 'REPORTS') {
        const hasAnyReportPermission =
          canView('REPORTS') ||
          canView('REPORTS_OVERVIEW') ||
          canView('REPORTS_CLIENTS') ||
          canView('REPORTS_REVENUE') ||
          canView('REPORTS_INGREDIENTS') ||
          canView('REPORTS_DIETS') ||
          canView('REPORTS_CHEF_COUNT') ||
          canView('REPORTS_FILING');
        return hasAnyReportPermission ? item : null;
      }

      // Convert to uppercase to match permission page names (e.g., 'dashboard' -> 'DASHBOARD')
      return canView(pageNameUpper) ? item : null;
    }
  }).filter(Boolean) as typeof navigation;

  const toggleExpanded = (name: string) => {
    setExpandedItems(prev =>
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name]
    );
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const isParentActive = (children: any[]) => {
    return children.some(child => isActive(child.href));
  };

  return (
    <div className="flex h-full flex-col bg-white shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          {settings?.logoUrl ? (
            <img src={settings.logoUrl} alt="Logo" className="h-8" />
          ) : (
            <h1 className="text-2xl font-bold text-primary-600">
              {(settings?.systemName || 'PROBOXLB').split(' ').map(w => w[0]).join('').toUpperCase()}
            </h1>
          )}
          <span className="text-sm text-gray-600 truncate max-w-[10rem]">{settings?.systemName || 'PROBOXLB'}</span>
        </div>
        {onClose && (
          <button
            type="button"
            className="lg:hidden text-gray-400 hover:text-gray-600"
            onClick={onClose}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* User info */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-600">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-gray-900">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-xs text-gray-500">{user?.username}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {filteredNavigation.map((item) => {
          if (item.children) {
            const isExpanded = expandedItems.includes(item.name);
            const hasActiveChild = isParentActive(item.children);

            return (
              <div key={item.name}>
                <button
                  onClick={() => toggleExpanded(item.name)}
                  className={clsx(
                    'w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                    hasActiveChild || isExpanded
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <div className="flex items-center">
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </div>
                  <ChevronDownIcon
                    className={clsx(
                      'h-4 w-4 transition-transform duration-200',
                      isExpanded ? 'rotate-180' : ''
                    )}
                  />
                </button>
                
                {isExpanded && (
                  <div className="mt-1 ml-8 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        onClick={onClose}
                        className={clsx(
                          'block px-3 py-2 text-sm rounded-lg transition-colors duration-200',
                          isActive(child.href)
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={onClose}
              className={clsx(
                'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                isActive(item.href)
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              <item.icon className="mr-3 h-5 w-5" />
              {item.name}
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                <SidebarContent onClose={onClose} />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
        <SidebarContent />
      </div>
    </>
  );
};

export default Sidebar;
